package com.nonopn.skydome.common.app.api.impl

import kotlinx.serialization.Serializable

// Models of the admin estimates routes (GET / POST / PATCH /api/admin/estimates, spec of 2026-10-24).
// Every field of the API is declared (the JSON client may refuse unknown keys). Enumerations are kept as
// strings so that a new value added by the server never breaks the decoding.

@Serializable
data class AdminAccount(
    val email: String? = null,
    val uuid: String? = null,
)

/** Anomaly detected by the server on an estimate; `code` is an `EstimateIssueCode`. */
@Serializable
data class AdminEstimateIssue(
    val code: String,
    val message: String = "",
    val probe: String? = null,
)

@Serializable
data class AdminSubscription(
    // Active | Trialing | PaymentIssue | Ended
    val state: String,
    val cancelsAtPeriodEnd: Boolean = false,
    val currentPeriodEndsAtSecondsUtc: Long? = null,
    val product: String? = null,
    val trialEndsAtSecondsUtc: Long? = null,
)

/** `ApiProbe` of the API. */
@Serializable
data class AdminApiProbe(
    val uuid: String,
    val name: String,
    val latitude: Double,
    val longitude: Double,
    val createdAtSecondsUtc: Long = 0L,
    val isDemo: Boolean = false,
    val radiusKm: Int = 0,
    // Demo | Subscription | ExternalContract
    val source: String? = null,
    // Active | Inactive | Deleted
    val status: String? = null,
    val validUntilSecondsUtc: Long? = null,
)

@Serializable
data class AdminEstimate(
    val uuid: String,
    val account: AdminAccount = AdminAccount(),
    // StrikeRadar | SkySentinel
    val app: String = "StrikeRadar",
    // Demo | External | Stripe
    val payment: String = "Demo",
    // Empty | Demo | DemoExpired | ExternalActive | ExternalPartial | ExternalDisabled | ExternalExpired |
    // PendingPayment | Active | PaymentIssue | Ended
    val state: String = "",
    val issues: List<AdminEstimateIssue> = emptyList(),
    val probes: List<AdminApiProbe> = emptyList(),
    val active: Boolean? = null,
    val validUntilSecondsUtc: Long? = null,
    val createdAtSecondsUtc: Long? = null,
    val paymentProduct: String? = null,
    val subscription: AdminSubscription? = null,
)

/** What the list needs from an [AdminEstimate], attached to the estimate shown by the list. */
@Serializable
data class AdminEstimateInfo(
    val state: String,
    val payment: String,
    val app: String = "StrikeRadar",
    val issues: List<AdminEstimateIssue> = emptyList(),
    val createdAtSecondsUtc: Long? = null,
    val paymentProduct: String? = null,
    val subscription: AdminSubscription? = null,
)

@Serializable
data class AdminProbePatch(
    val uuid: String,
    val active: Boolean,
)

/** Body of PATCH /api/admin/estimates/{uuid}: only what is sent changes (External only for `active`). */
@Serializable
data class AdminEstimatePatch(
    val active: Boolean? = null,
    val probes: List<AdminProbePatch>? = null,
    val validUntilSecondsUtc: Long? = null,
    val removeValidUntil: Boolean? = null,
)
